import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_FAULT_TICKET_ROUTER } from "../../../services/api-config";

/** Format ngày giờ dd/MM/yyyy HH:mm (coi datetime từ BE là UTC → giờ local) */
export const formatDateTime = (raw?: string | null): string => {
  if (!raw) return "—";
  const s = String(raw);
  const d = new Date(s.endsWith("Z") || s.includes("+") ? s : s + "Z");
  if (isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// ===== Enums (khớp BE order service) =====
export enum FAULT_TYPE {
  GIAO_TRE = 1,
  THIEU_DO = 2,
  HU_HONG = 3,
  KHONG_NOP_QUY = 4,
  SAI_QUY_TRINH = 5,
  QUA_HAN_XAC_NHAN = 6,
  KHONG_DIEU_PHOI = 7,
  QUA_HAN_SLA = 8,
  NO_DO_QUA_HAN = 9,
  SAI_VI_TRI_GIAO = 10,
  KHAC = 99,
}
export const FAULT_TYPE_LABEL: Record<number, string> = {
  [FAULT_TYPE.GIAO_TRE]: "Giao trễ",
  [FAULT_TYPE.THIEU_DO]: "Thiếu đồ giặt",
  [FAULT_TYPE.HU_HONG]: "Hư hỏng đồ",
  [FAULT_TYPE.KHONG_NOP_QUY]: "Không nộp quỹ",
  [FAULT_TYPE.SAI_QUY_TRINH]: "Sai quy trình",
  [FAULT_TYPE.QUA_HAN_XAC_NHAN]: "Quá hạn xác nhận",
  [FAULT_TYPE.KHONG_DIEU_PHOI]: "Không điều phối đơn",
  [FAULT_TYPE.QUA_HAN_SLA]: "Quá hạn tiến độ (SLA)",
  [FAULT_TYPE.NO_DO_QUA_HAN]: "Nợ đồ quá hạn",
  [FAULT_TYPE.SAI_VI_TRI_GIAO]: "Hoàn thành sai vị trí",
  [FAULT_TYPE.KHAC]: "Khác",
};

export enum FAULT_SEVERITY {
  LIGHT = 1,
  MEDIUM = 2,
  HEAVY = 3,
}
export const FAULT_SEVERITY_LABEL: Record<number, string> = {
  [FAULT_SEVERITY.LIGHT]: "Nhẹ",
  [FAULT_SEVERITY.MEDIUM]: "Vừa",
  [FAULT_SEVERITY.HEAVY]: "Nặng",
};

export enum FAULT_STATUS {
  OPEN = 1,
  ACKNOWLEDGED = 2,
  RESOLVED = 3,
  REJECTED = 4,
}
export const FAULT_STATUS_LABEL: Record<number, string> = {
  [FAULT_STATUS.OPEN]: "Chờ xử lý",
  [FAULT_STATUS.ACKNOWLEDGED]: "Đã giải trình",
  [FAULT_STATUS.RESOLVED]: "Đã ghi lỗi",
  [FAULT_STATUS.REJECTED]: "Hủy lỗi",
};

/**
 * Trạng thái đồng bộ sang hệ thống đối tác.
 * FAILED và NEEDS_CHECK khác nhau ở chỗ: FAILED là chắc chắn bên kia chưa ghi (cron tự gửi lại),
 * NEEDS_CHECK là không biết đã ghi hay chưa (timeout) → chỉ người xác nhận mới được bấm gửi lại.
 */
export enum FAULT_SYNC_STATUS {
  NOT_SENT = 0,
  SENT = 1,
  FAILED = 2,
  NEEDS_CHECK = 3,
}
export const FAULT_SYNC_STATUS_LABEL: Record<number, string> = {
  [FAULT_SYNC_STATUS.NOT_SENT]: "Chưa gửi",
  [FAULT_SYNC_STATUS.SENT]: "Đã gửi",
  [FAULT_SYNC_STATUS.FAILED]: "Gửi lỗi",
  [FAULT_SYNC_STATUS.NEEDS_CHECK]: "Cần kiểm tra",
};
export const FAULT_SYNC_STATUS_CLASS: Record<number, string> = {
  [FAULT_SYNC_STATUS.NOT_SENT]: "bg-gray-100 text-gray-600",
  [FAULT_SYNC_STATUS.SENT]: "bg-green-50 text-green-700",
  [FAULT_SYNC_STATUS.FAILED]: "bg-red-50 text-red-600",
  [FAULT_SYNC_STATUS.NEEDS_CHECK]: "bg-amber-50 text-amber-700",
};

export enum FAULT_SOURCE {
  MANUAL = 1,
  SYSTEM = 2,
}
export const FAULT_SOURCE_LABEL: Record<number, string> = {
  [FAULT_SOURCE.MANUAL]: "Tạo tay",
  [FAULT_SOURCE.SYSTEM]: "Hệ thống",
};

// ===== Căn cứ đối soát SL của phiếu thất thoát đồ (khớp BE `fault_tickets.shortage_data`) =====
export const SHORTAGE_REASON_LABEL: Record<string, string> = {
  LOST: "Thất thoát",
  NON_STANDARD: "Hàng không chuẩn",
  OWE_CUSTOMER: "Nợ khách (chưa giặt kịp)",
  RETURN_DEBT: "Trả nợ (dư)",
};

export interface ShortageData {
  stage_from: string;
  stage_to: string;
  stage_from_label: string;
  stage_to_label: string;
  from_at: string | null;
  to_at: string | null;
  expected_total: number;
  actual_total: number;
  missing_total: number;
  missing_items: { category: string; quantity: number }[];
  reason?: string | null;
  reason_note?: string | null;
  reported_by?: number;
}

export interface FaultTicket {
  id: number;
  code: string;
  order_id: number;
  branch_id: number;
  fault_type: number;
  severity: number;
  description: string | null;
  images: string[] | null;
  responsible_user_id: number;
  reported_by: number;
  source_type: number;
  explanation: string | null;
  status: number;
  penalty_amount: number | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  // Phiếu SLA tự động
  penalty_points?: number | null;
  escalation_level?: number | null; // lần leo thang (1/2/3)
  milestone_code?: string | null;
  // Cửa sổ THỰC HIỆN BƯỚC — căn cứ phạt (snapshot lúc tạo phiếu)
  sla_from_at?: string | null;         // lúc đơn vào bước
  sla_to_at?: string | null;           // lúc đơn rời bước; null = chưa hoàn thành
  sla_target_hours?: number | null;    // ngưỡng quy định của mốc
  sla_elapsed_minutes?: number | null; // số phút đã thực hiện bước
  // Phiếu THẤT THOÁT ĐỒ tự động — căn cứ đối soát SL (snapshot lúc tạo phiếu)
  shortage_data?: ShortageData | null;
  // Đồng bộ sang hệ thống đối tác
  sync_status?: number;
  synced_at?: string | null;
  sync_error?: string | null;
  // Enrich từ BE
  branch_name?: string | null;
  responsible_name?: string | null;
  reporter_name?: string | null;
  order_code?: string | null;
  milestone_name?: string | null; // tên mốc SLA (đã có loại đơn)
}

/** Đổi số phút → "3h27p" (dưới 1 giờ chỉ hiện phút). */
export const formatMinutes = (minutes?: number | null): string => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h${m % 60 > 0 ? `${m % 60}p` : ""}` : `${m}p`;
};

/** Số phút VƯỢT ngưỡng quy định (thời gian thực hiện − target). Null nếu phiếu không phải SLA. */
export const getOverdueMinutes = (t: {
  sla_elapsed_minutes?: number | null;
  sla_target_hours?: number | null;
}): number | null => {
  if (t.sla_elapsed_minutes == null || t.sla_target_hours == null) return null;
  return Math.max(0, Number(t.sla_elapsed_minutes) - Number(t.sla_target_hours) * 60);
};

export interface FaultTicketListResponse {
  list: FaultTicket[];
  total_record: number;
  page: number;
  limit: number;
}

export interface FaultTicketFilters {
  branch_id?: number;
  status?: number;
  fault_type?: number;
  responsible_user_id?: number;
  order_id?: number;
  key_search?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
}

export interface CreateFaultTicketPayload {
  branch_id: number;
  fault_type: number;
  responsible_user_id: number;
  order_code?: string;
  severity?: number;
  description?: string;
  images?: string[];
  /** ĐIỂM phạt — BE quy ra tiền theo CONFIG_POINT_TO_VND. Không gửi tiền trực tiếp nữa. */
  penalty_points?: number;
}

export interface UpdateFaultTicketPayload {
  status?: number;
  fault_type?: number;
  severity?: number;
  description?: string;
  penalty_amount?: number;
}

// ===== Báo cáo phiếu lỗi =====
export interface FaultReportTicket {
  id: number;
  code: string;
  fault_type: number;
  milestone_name?: string | null;
  escalation_level?: number | null;
  penalty_points: number;
  penalty_amount: number;
  status: number;
  created_at: string;
  order_code?: string | null;
  // Cửa sổ thực hiện bước của mốc SLA (chỉ phiếu tự động)
  sla_from_at?: string | null;
  sla_to_at?: string | null;
  sla_target_hours?: number | null;
  sla_elapsed_minutes?: number | null;
}
export interface FaultReportEmployee {
  employee_id: number;
  employee_name: string;
  total_tickets: number;
  total_points: number;
  total_amount: number;
  tickets: FaultReportTicket[];
}
export interface FaultReportBranch {
  branch_id: number;
  branch_name: string;
  total_tickets: number;
  total_points: number;
  total_amount: number;
  by_type: { fault_type: number; count: number }[];
  employees: FaultReportEmployee[];
}
export interface FaultReportResponse {
  summary: {
    total_tickets: number;
    total_points: number;
    total_amount: number;
    branch_count: number;
    employee_count: number;
  };
  branches: FaultReportBranch[];
  by_type: { fault_type: number; count: number; points: number; amount: number }[];
}
export interface FaultReportFilters {
  start_date?: string;
  end_date?: string;
  branch_id?: number;
  employee_id?: number;
}

/** Kết quả một lượt đồng bộ (BE trả về sau khi gửi xong các lô) */
export interface SyncOutcome {
  sent: number;
  failed: number;
  needsCheck: number;
}

/**
 * Tiến độ phiên đồng bộ tay. `run` chạy nền ở BE (một lô có thể chờ đối tác 20s nên chạy đồng bộ
 * sẽ bị nginx cắt 504), nên kết quả phải đọc bằng cách poll `status` cho tới khi `running=false`.
 */
export interface SyncProgress extends SyncOutcome {
  running: boolean;
  /** Giờ VN `Y-m-d H:i:s`. */
  started_at: string | null;
  finished_at: string | null;
  /** Tổng phiếu được chọn cho phiên này; 0 khi chưa quét xong. */
  total: number;
  processed: number;
  /** Lỗi làm phiên dừng giữa chừng (không phải lỗi từng phiếu). */
  error: string | null;
}

/** Response của `run`: có khởi động được phiên mới không, kèm tiến độ hiện tại. */
export interface SyncStartResult extends SyncProgress {
  /** `false` khi đã có phiên đang chạy — không khởi động phiên thứ hai. */
  started: boolean;
  message: string;
}

const FaultTicketService = {
  report: () => {
    const { request, baseResponse, loading } = useFetchData.Get<FaultReportResponse>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: FaultReportFilters = {}) =>
        request({ endPoint: API_FAULT_TICKET_ROUTER.API_REPORT, data: params }),
      response: baseResponse,
      loading,
    };
  },
  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<FaultTicketListResponse>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: FaultTicketFilters = {}) =>
        request({
          endPoint: API_FAULT_TICKET_ROUTER.API_LIST,
          data: {
            ...params,
            page: params.page ?? 1,
            limit: params.limit ?? 20,
          },
        }),
      response: baseResponse,
      loading,
    };
  },
  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<FaultTicket>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (payload: CreateFaultTicketPayload) =>
        request({ endPoint: API_FAULT_TICKET_ROUTER.API_CREATE, data: payload }),
      response: baseResponse,
      loading,
    };
  },
  detail: () => {
    const { request, baseResponse, loading } = useFetchData.Get<FaultTicket>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) => request({ endPoint: API_FAULT_TICKET_ROUTER.API_DETAIL(id) }),
      response: baseResponse,
      loading,
    };
  },
  update: () => {
    const { request, baseResponse, loading } = useFetchData.Post<FaultTicket>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, payload: UpdateFaultTicketPayload) =>
        request({ endPoint: API_FAULT_TICKET_ROUTER.API_UPDATE(id), data: payload }),
      response: baseResponse,
      loading,
    };
  },
  /**
   * Bắt đầu đồng bộ tay sang hệ thống đối tác (phiếu đã chốt chưa gửi trong 10 ngày gần nhất).
   * Trả về ngay; phải poll `syncStatus` để biết kết quả.
   */
  syncRun: () => {
    const { request, baseResponse, loading } = useFetchData.Post<SyncStartResult>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: () => request({ endPoint: API_FAULT_TICKET_ROUTER.API_SYNC_RUN, data: {} }),
      response: baseResponse,
      loading,
    };
  },
  /** Tiến độ phiên đồng bộ tay gần nhất */
  syncStatus: () => {
    const { request, baseResponse, loading } = useFetchData.Get<SyncProgress>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: () => request({ endPoint: API_FAULT_TICKET_ROUTER.API_SYNC_STATUS }),
      response: baseResponse,
      loading,
    };
  },
  /** Gửi lại một phiếu (đường duy nhất cho phiếu "cần kiểm tra") */
  syncRetry: () => {
    const { request, baseResponse, loading } = useFetchData.Post<SyncOutcome>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (faultTicketId: number) =>
        request({
          endPoint: API_FAULT_TICKET_ROUTER.API_SYNC_RETRY,
          data: { fault_ticket_id: faultTicketId },
        }),
      response: baseResponse,
      loading,
    };
  },
};

export default FaultTicketService;
